<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import Icon from './ui/Icon.vue'
import Kbd from './ui/Kbd.vue'
import { ariaCombo, keysOf } from '../lib/shortcuts'
import TransactionRow from './TransactionRow.vue'
import {
  fmt,
  fmtGlobal,
  idsInView,
  listTotals,
  loadMore,
  setSort,
  SORTS,
  transactionCount,
  transactionDays,
  visibleTotals,
} from '../stores'
import { openSheet, state, stopSelecting, toast } from '../stores/state'
import { deleteTransactions } from '../stores/bulk'
import { dayLabel } from '../lib/dates'
import { t } from '../stores/i18n'

// The list holds a page or a few of the view; the counts are the whole view's.
// Savings are shown but never acted on, so "all" and "edit all" pass them by —
// while "shown" means every row, savings included.
const inView = computed(() => visibleTotals.value.count)

/**
 * The end of what has been read: when it comes near the screen, the next page
 * is asked for. Observed against the viewport, which also sees through the
 * list's own scroller on a wide screen — an element that scroller clips is not
 * on screen.
 */
const sentinel = ref(null)
let observer = null
watch(sentinel, (el, old) => {
  if (old) observer?.unobserve(old)
  if (!el || typeof IntersectionObserver === 'undefined') return
  observer ??= new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && loadMore(), {
    rootMargin: '600px 0px',
  })
  observer.observe(el)
})
onBeforeUnmount(() => observer?.disconnect())
const sorting = ref(false)
const sortLabel = computed(
  () => SORTS.find((s) => s.key === state.ui.sort.by)?.label ?? 'Date',
)
const pickSort = (key) => {
  setSort(key)
  sorting.value = false
}
const allChosen = computed(() => inView.value > 0 && state.ui.selected.length >= inView.value)

/**
 * The check-all takes in exactly what the search and filters have left —
 * every row of it, read or not yet read.
 */
async function toggleAll() {
  state.ui.selected = allChosen.value ? [] : await idsInView()
}

/** Select, all, edit — in one tap, since that is nearly always the sequence. */
async function editAll() {
  state.ui.selecting = true
  state.ui.selected = await idsInView()
  openSheet('bulk')
}

/**
 * Deleting what is selected, from the selection bar itself rather than from
 * inside the edit sheet — deleting is not a kind of editing, and going through
 * "Edit" to get to it is a detour.
 *
 * Two taps, like every other delete here. The primed state is dropped whenever
 * the selection changes, so a Delete left armed cannot be spent on rows it was
 * never aimed at.
 */
const confirmDelete = ref(false)
const deleting = ref(false)
watch(
  () => [state.ui.selected.join(), state.ui.selecting],
  () => (confirmDelete.value = false),
)

async function destroySelected() {
  if (!state.ui.selected.length) return
  if (!confirmDelete.value) {
    confirmDelete.value = true
    return
  }
  deleting.value = true
  const count = await deleteTransactions(state.ui.selected)
  deleting.value = false
  toast(t('bulk.deleted', { count }))
  stopSelecting()
}
</script>

<template>
  <section class="card relative overflow-visible lg:flex lg:h-full lg:flex-col">
    <header class="flex shrink-0 items-center gap-3 border-b border-line-soft px-4 py-3.5">
      <div class="min-w-0 flex-1">
        <h2 class="text-[15px] font-semibold">{{ t('ledger.title') }}</h2>
        <p class="tnum mt-0.5 flex items-center gap-1 truncate text-[12px] text-faint">
          <span class="truncate">
            {{ t('ledger.shown', { count: listTotals.shown }) }} ·
            <span class="text-accent">
              {{ t('ledger.in', { amount: fmtGlobal(visibleTotals.income, { round: true }) }) }}
            </span> ·
            {{ t('ledger.out', { amount: fmtGlobal(visibleTotals.spent, { round: true }) }) }}
          </span>
          <span aria-hidden="true">·</span>
          <button
            class="inline-flex shrink-0 items-center gap-1 rounded px-1 py-0.5 text-[12px] text-muted
                   transition hover:text-text"
            :aria-label="t('ledger.sortedBy', {
              sort: sortLabel.toLowerCase(),
              dir: state.ui.sort.dir === 'asc' ? t('ledger.ascending') : t('ledger.descending'),
            })"
            :aria-expanded="sorting"
            @click.stop="sorting = !sorting"
          >
            <Icon name="sort" :size="12" />
            {{ t(`sort.${state.ui.sort.by}`) }}
            <span aria-hidden="true">{{ state.ui.sort.dir === 'asc' ? '↑' : '↓' }}</span>
          </button>
        </p>
      </div>
      <!-- Room for three on a phone by dropping the words, not the buttons:
           the label stays in the accessibility tree either way. -->
      <template v-if="!state.ui.selecting">
        <button
          class="btn-soft !px-2.5 !py-2 sm:!px-3.5"
          :disabled="!inView"
          :aria-keyshortcuts="ariaCombo(keysOf('select'))"
          @click="state.ui.selecting = true"
        >
          <Icon name="check" :size="16" /><span class="sr-only sm:not-sr-only">{{ t('ledger.select') }}</span>
          <Kbd :combo="keysOf('select')" />
        </button>
        <button
          class="btn-soft !px-2.5 !py-2 sm:!px-3.5"
          :disabled="transactionCount < 2"
          @click="openSheet('duplicates')"
        >
          <Icon name="copy" :size="16" /><span class="sr-only sm:not-sr-only">{{ t('ledger.duplicates') }}</span>
        </button>
        <button class="btn-soft !px-2.5 !py-2 sm:!px-3.5" :disabled="!inView" @click="editAll">
          <Icon name="pencil" :size="16" /><span class="sr-only sm:not-sr-only">{{ t('ledger.editAll') }}</span>
        </button>
        <!-- The way in to a new transaction, with N from anywhere on the page. -->
        <button
          class="btn-soft !px-2.5 !py-2 sm:!px-3.5"
          :aria-label="t('ledger.addTransaction')"
          :aria-keyshortcuts="ariaCombo(keysOf('new'))"
          data-add-transaction
          @click="openSheet('transaction')"
        >
          <Icon name="plus" :size="16" /><span class="sr-only sm:not-sr-only">{{ t('common.add') }}</span>
          <Kbd :combo="keysOf('new')" />
        </button>
      </template>
      <button v-else class="btn-ghost !py-2" :aria-keyshortcuts="ariaCombo(keysOf('select'))" @click="stopSelecting">
        {{ t('common.done') }} <Kbd :combo="keysOf('select')" />
      </button>
    </header>

    <div
      v-if="sorting"
      class="absolute z-30 mt-1 ml-4 w-52 rounded-xl border border-line bg-surface p-1.5
             shadow-[var(--c-shadow)]"
      role="menu"
    >
      <button
        v-for="option in SORTS"
        :key="option.key"
        class="menu-row"
        :class="state.ui.sort.by === option.key && 'text-accent'"
        @click="pickSort(option.key)"
      >
        {{ t(`sort.${option.key}`) }}
        <span v-if="state.ui.sort.by === option.key" aria-hidden="true">
          {{ state.ui.sort.dir === 'asc' ? '↑' : '↓' }}
        </span>
      </button>
      <p class="px-2 pt-1 pb-0.5 text-[11.5px] text-faint">{{ t('ledger.flipHint') }}</p>
    </div>

    <div
      v-if="state.ui.selecting"
      class="flex shrink-0 flex-wrap items-center gap-2 border-b border-line-soft bg-raised/60 px-4 py-2"
    >
      <button class="chip !py-1 !text-[12.5px]" :class="{ 'chip-on': allChosen }" @click="toggleAll">
        <Icon name="check" :size="13" /> {{ allChosen ? t('ledger.none') : t('ledger.all') }}
      </button>
      <span class="tnum text-[12.5px] text-muted">
        {{ t('ledger.selected', { n: state.ui.selected.length, of: inView }) }}
      </span>
      <div class="flex-1" />
      <button
        class="btn-danger !px-3 !py-1.5 !text-[12.5px]"
        :disabled="!state.ui.selected.length || deleting"
        @click="destroySelected"
      >
        <Icon name="trash" :size="14" />
        {{ confirmDelete ? t('common.confirmDelete') : t('common.delete') }}
      </button>
      <button
        class="btn-soft !px-3 !py-1.5 !text-[12.5px]"
        :disabled="!state.ui.selected.length"
        @click="openSheet('bulk')"
      >
        <Icon name="pencil" :size="14" /> {{ t('common.edit') }}
      </button>
    </div>

    <!-- min-h-0 or a flex child refuses to shrink below its content and the
         card grows past the column instead of scrolling. -->
    <div v-if="transactionDays.length" class="scroll-thin lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
      <div v-for="day in transactionDays" :key="day.date ?? 'all'">
        <div
          v-if="day.date"
          class="z-10 flex items-center justify-between bg-surface/95 px-4 py-1.5 text-[11.5px]
                 font-semibold tracking-wide text-faint uppercase backdrop-blur lg:sticky lg:top-0"
        >
          <span>{{ dayLabel(day.date) }}</span>
          <span class="tnum" :class="day.total > 0 ? 'text-accent' : ''">
            {{ fmt(day.total, { signed: true }) }}
          </span>
        </div>
        <TransactionRow
          v-for="tx in day.items"
          :key="tx.id"
          :tx="tx"
          :show-date="!day.date"
        />
      </div>
      <!-- More is read as this comes into view; the button is the same thing
           for a keyboard, or a browser without IntersectionObserver. -->
      <div v-if="!state.ledger.done" ref="sentinel" class="px-4 py-3 text-center" data-more>
        <button class="btn-ghost !py-1.5 !text-[12.5px]" :disabled="state.ledger.loading" @click="loadMore">
          {{ state.ledger.loading ? t('ledger.loading') : t('ledger.more') }}
        </button>
      </div>
    </div>

    <!-- Nothing to list still fills the column, so the card does not end
         halfway up beside the panels. -->
    <div v-else class="px-6 py-14 text-center lg:flex lg:flex-1 lg:flex-col lg:justify-center">
      <span class="mx-auto grid size-12 place-items-center rounded-2xl bg-raised text-faint">
        <Icon name="search" :size="22" />
      </span>
      <p class="mt-3 text-[14px] font-medium">
        {{ state.ui.search ? t('ledger.emptySearch') : t('ledger.empty') }}
      </p>
      <p class="mt-1 text-[13px] text-muted">
        {{ state.ui.search ? t('ledger.emptySearchHint') : t('ledger.emptyHint') }}
      </p>
      <button class="btn-primary mx-auto mt-4" @click="openSheet('transaction')">
        <Icon name="plus" :size="16" /> {{ t('ledger.addTransaction') }}
      </button>
    </div>
  </section>
</template>
